/**
 * Subscription API Service
 * Connects frontend to backend subscription endpoints
 */
import { apiClient } from './apiClient';

// Subscription Types
export interface SubscriptionPlan {
  plan_id: number;
  plan_name: string;
  plan_display_name: string;
  plan_description: string | null;
  monthly_price: number;
  annual_price: number | null;
  plan_order: number;
  is_trial_plan: boolean;
  allows_trial: boolean;
}

export interface CurrentSubscription {
  subscription_id: number;
  rest_account_id: number;
  restaurant_name: string;
  plan_name: string;
  plan_display_name: string;
  monthly_price: number;
  subscription_status: string;
  is_trial: boolean;
  trial_days_remaining: number | null;
  trial_expired: boolean;
  billing_cycle: string;
  auto_renew: boolean;
  payment_method_type: string | null;
}

export interface FeatureAccess {
  feature_key: string;
  has_access: boolean;
  usage_limit: number | null;
  usage_period: string | null;
  feature_config: Record<string, any>;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  subscription?: CurrentSubscription;
  features?: Array<{
    feature_key: string;
    feature_name: string;
    usage_limit: number | null;
    feature_config: Record<string, any>;
  }>;
  error?: string;
}

export interface TrialEligibilityCheck {
  email: string;
  phone?: string;
  business_tax_id?: string;
  ip_address?: string;
  device_fingerprint?: string;
}

export interface TrialEligibilityResponse {
  is_eligible: boolean;
  reason: string;
}

export const subscriptionApi = {
  /**
   * Get all available subscription plans
   */
  async getPlans(): Promise<SubscriptionPlan[]> {
    const response = await apiClient.get('/subscriptions/plans');
    return response.data;
  },

  /**
   * Get current user's subscription details
   */
  async getCurrentSubscription(): Promise<CurrentSubscription> {
    const response = await apiClient.get('/subscriptions/current');
    return response.data;
  },

  /**
   * Get detailed subscription status for dashboard
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    const response = await apiClient.get('/subscriptions/status');
    return response.data;
  },

  /**
   * Check if user has access to a specific feature
   */
  async checkFeatureAccess(featureKey: string): Promise<FeatureAccess> {
    const response = await apiClient.get(`/subscriptions/features/check/${featureKey}`);
    return response.data;
  },

  /**
   * Get features for a specific plan
   */
  async getPlanFeatures(planName: string): Promise<any[]> {
    const response = await apiClient.get(`/subscriptions/plans/${planName}/features`);
    return response.data;
  },

  /**
   * Check trial eligibility
   */
  async checkTrialEligibility(request: TrialEligibilityCheck): Promise<TrialEligibilityResponse> {
    const response = await apiClient.post('/subscriptions/trial/check-eligibility', request);
    return response.data;
  },

  /**
   * Get revenue analytics (admin endpoint)
   */
  async getRevenueAnalytics(): Promise<any[]> {
    const response = await apiClient.get('/subscriptions/analytics/revenue');
    return response.data;
  },

  /**
   * Get trial conversion analytics (admin endpoint)
   */
  async getConversionAnalytics(): Promise<any[]> {
    const response = await apiClient.get('/subscriptions/analytics/conversion');
    return response.data;
  },

  /**
   * Development/Testing: Get all test data
   */
  async getAllTestData(): Promise<any> {
    const response = await apiClient.get('/subscriptions/test/all-data');
    return response.data;
  },

  /**
   * Development/Testing: Get plan comparison
   */
  async getPlanComparison(): Promise<any[]> {
    const response = await apiClient.get('/subscriptions/test/plan-comparison');
    return response.data;
  },
};

// Helper function to determine subscription features for UI
export const getSubscriptionFeatures = (subscription: CurrentSubscription | null) => {
  if (!subscription) {
    return {
      tier: 'None',
      hasMenuAssistant: false,
      hasPOSIntegration: false,
      hasAnalytics: false,
      hasAutoPromotions: false,
      hasAIImages: false,
      hasAdvancedInsights: false,
      hasAPIAccess: false,
      hasWhiteLabel: false,
      isTrialExpired: false,
      trialDaysRemaining: null,
    };
  }

  const planName = subscription.plan_name.toLowerCase();
  
  return {
    tier: subscription.plan_display_name,
    planName: subscription.plan_name,
    monthlyPrice: subscription.monthly_price,
    isTrial: subscription.is_trial,
    isTrialExpired: subscription.trial_expired,
    trialDaysRemaining: subscription.trial_days_remaining,
    subscriptionStatus: subscription.subscription_status,
    
    // Feature access based on plan (matches backend plan_features)
    hasMenuAssistant: true, // All plans
    hasMenuManagement: true, // All plans
    hasPOSIntegration: false, // Feature deprecated
    hasAnalytics: planName !== 'yummy', // Trial, Delicious, Gourmet
    hasAutoPromotions: planName === 'gourmet', // Gourmet only
    hasAIImages: planName !== 'yummy', // Trial, Delicious, Gourmet
    hasAdvancedInsights: planName === 'gourmet', // Gourmet only
    hasAPIAccess: planName === 'gourmet', // Gourmet only
    hasWhiteLabel: planName === 'gourmet', // Gourmet only
  };
};